/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React from 'react';
import type { AxiosError } from 'axios';
import type { FormikErrors, FormikHelpers } from 'formik';
import type {
  Guardian,
  RosterManagementStudentListItem,
} from '@whizz/api-model';
import type { ToggleModalProps } from '../../../../../components/modal/types';
import { useI18n } from '@whizz/react-i18n';
import { Button, Input } from '../../../../../components';
import { CancelButtonModal, FormGenericError, SaveButton } from '../../../../../global';
import { GLOBAL_ICONS } from '../../../../../util/vars';
import { model } from '@whizz/api-client';
import { useMutation } from 'react-query';
import { FieldArray, Formik } from 'formik';
import * as Yup from 'yup';
import { ModalFooter } from '../../../../../components/modal/Modal';
import { useCleverIntegration } from '../../../../../hooks/useCleverIntegration';

export interface AddEditParentModalProps extends ToggleModalProps {
  student: RosterManagementStudentListItem & { guardians: Guardian[] };
  onSuccess?: () => void;
  onError?: () => void;
}

const MIN_LABEL_WIDTH = 150;
const MAX_GUARDIANS = 3;

const INITIAL_VALUES = {
  guardians: [],
};

const AddEditParentsModal = ({ student, toggle, onSuccess, onError }: AddEditParentModalProps): JSX.Element => {
  const { translate } = useI18n();
  const [ isClever ] = useCleverIntegration();

  const updateMutation = useMutation(
    ({ formData } : { formData: FormData, formikHelpers: FormikHelpers<FormData> }): Promise<Guardian[]> => {
      return model.Guardians.putByIdFromSchool(student!.id, formData.guardians );
    },
    {
      onSuccess: () => {
        onSuccess?.();
        toggle();
      },
      onError: (error: AxiosError<{ error: string }>) => {
        if (null == error.response) {
          return;
        }

        onError?.();
      },
    },
  );

  const handleSubmit = (formData: FormData, formikHelpers: FormikHelpers<FormData>) => {
    const castedFormData = VALIDATION_SCHEMA.cast(formData) as FormData;
    updateMutation.mutate({
      formData:{
        guardians: castedFormData.guardians ?? [],
      },
      formikHelpers,
    });
  };

  const isSubmitting = updateMutation.isLoading;

  const studentData: FormData | null = null != student ? { guardians: student.guardians } : null;

  const VALIDATION_SCHEMA = Yup.object()
    .shape({
      guardians: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().required(translate('pages.ROSTER_MANAGEMENT.PARENTS.ADD_EDIT.VALIDATIONS.NAME.REQUIRED')),
          email: Yup.string()
            .email(translate('pages.ROSTER_MANAGEMENT.PARENTS.ADD_EDIT.VALIDATIONS.EMAIL.REQUIRED'))
            .required(translate('pages.ROSTER_MANAGEMENT.PARENTS.ADD_EDIT.VALIDATIONS.EMAIL.REQUIRED'))
            .test('is-unique', translate('pages.ROSTER_MANAGEMENT.PARENTS.ADD_EDIT.VALIDATIONS.EMAIL.ALREADY_EXISTS'), function(value) {
              const { guardians } = this.from?.[1].value;
              const emailCount = guardians?.filter((guardian: { email: string }) => guardian.email === value).length ?? 1;
              return emailCount === 1;
            }),
        }),
      ),
    });

  return (
    <Formik <FormData>
      initialValues={Object.assign({ ...INITIAL_VALUES },  studentData)}
      initialErrors={{}}
      validationSchema={VALIDATION_SCHEMA}
      onSubmit={(formData, formikHelpers) => handleSubmit(formData, formikHelpers)}
    >
      {({ handleSubmit, values, getFieldProps, errors, isValid, touched, resetForm, setFieldValue, initialValues }) => {

        const noExistingOrNewGuardians = initialValues.guardians.length === 0 && values.guardians.length === 0;

        if (noExistingOrNewGuardians) {
          setFieldValue('guardians', [ { name: '', email: '' } ]);
        }

        const hasErrors = values.guardians.some((guardian, index) => {
          const guardianErrors = errors.guardians?.[index] as FormikErrors<Guardian> | undefined;
          const guardianTouched = touched.guardians?.[index] || {};
          const nameError = guardianTouched.name ? guardianErrors?.name : null;
          const emailError = guardianTouched.email ? guardianErrors?.email : null;

          return !!(nameError || emailError);
        });

        return (
          <form onSubmit={handleSubmit}>
            <div className={'container space-y-6'}>
              <FieldArray
                name={'guardians'}
                render={({ push, remove }) => (
                  <>
                    {values.guardians.map((guardian, index) => {
                      const guardianErrors = errors.guardians?.[index] as FormikErrors<Guardian> | undefined;
                      const guardianTouched = touched.guardians?.[index] || {};
                      const nameError = guardianTouched.name ? guardianErrors?.name : null;
                      const emailError = guardianTouched.email ? guardianErrors?.email : null;

                      return (
                        <div key={`guardian-${index}`} className={'space-y-4'}>
                          <div className={'flex justify-between items-center'}>
                            <h3 className={'font-bold text-xl'}>{translate('pages.ROSTER_MANAGEMENT.PARENTS.ADD_EDIT.GUARDIAN', { index: index + 1 })}</h3>
                            <Button
                              icon={GLOBAL_ICONS.delete}
                              onClick={() => remove(index)}
                              color={'negative'}
                              isOutline
                            >
                              {translate('pages.ROSTER_MANAGEMENT.PARENTS.ADD_EDIT.BUTTONS.REMOVE_PARENT')}
                            </Button>
                          </div>
                          <div className={'space-y-2 ml-6'}>
                            <Input
                              {...getFieldProps(`guardians.${index}.name`)}
                              id={`guardians.${index}.name`}
                              label={translate('pages.ROSTER_MANAGEMENT.PARENTS.ADD_EDIT.NAME')}
                              labelStyle={'bold'}
                              minWidthForLabel={MIN_LABEL_WIDTH}
                              error={nameError}
                              required
                              disabled={isClever}
                            />
                            <Input
                              {...getFieldProps(`guardians.${index}.email`)}
                              id={`guardians.${index}.email`}
                              label={translate('pages.ROSTER_MANAGEMENT.PARENTS.ADD_EDIT.EMAIL')}
                              labelStyle={'bold'}
                              type={'email'}
                              minWidthForLabel={MIN_LABEL_WIDTH}
                              error={emailError}
                              required
                              disabled={isClever}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {values.guardians.length < MAX_GUARDIANS ? <Button
                      icon={GLOBAL_ICONS.add}
                      onClick={() => push({ name: '', email: '' })}
                      color={'positive'}
                      isOutline
                    >
                      {translate('pages.ROSTER_MANAGEMENT.PARENTS.ADD_EDIT.BUTTONS.ADD_PARENT')}
                    </Button> : null}
                  </>
                )}
              />
              {(!isValid && hasErrors)
              && (
                <FormGenericError />
              )}
            </div>
            <ModalFooter justify={'between'}>
              <CancelButtonModal
                align={'start'}
                onClick={() => resetForm()}
              >
                {translate('pages.ROSTER_MANAGEMENT.PARENTS.ADD_EDIT.BUTTONS.DISCARD')}
              </CancelButtonModal>
              <SaveButton
                type={'submit'}
                isLoading={isSubmitting}
              />
            </ModalFooter>
          </form>
        );
      }}
    </Formik>
  );
};

export default AddEditParentsModal;

export interface FormData {
  guardians: Guardian[];
}

